import React from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Save, Close } from "@mui/icons-material";
import { DialogShell } from "@/shared/ui/DialogShell";
import { EntryDiff, TranscriptDiff, WordToken } from "../utils/transcriptDiff";
import { formatTimestamp } from "../utils/timeFormat";
import { formatSpeakerLabel } from "./speakerColors";

interface SaveDiffDialogProps {
  open: boolean;
  diff: TranscriptDiff;
  isSaving: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const kindMeta = {
  added: { label: "Added", color: "success" as const },
  removed: { label: "Removed", color: "error" as const },
  modified: { label: "Modified", color: "warning" as const },
};

/** Inline word-level diff: additions highlighted, deletions struck through. */
const WordDiff: React.FC<{ tokens: WordToken[] }> = ({ tokens }) => {
  const theme = useTheme();
  return (
    <Typography
      component="div"
      variant="body2"
      sx={{ lineHeight: 1.7, whiteSpace: "pre-wrap" }}
    >
      {tokens.map((t, i) => {
        const sx =
          t.type === "added"
            ? {
                backgroundColor: alpha(theme.palette.success.main, 0.22),
                borderRadius: 0.5,
              }
            : t.type === "removed"
              ? {
                  backgroundColor: alpha(theme.palette.error.main, 0.16),
                  textDecoration: "line-through",
                  color: "text.secondary",
                  borderRadius: 0.5,
                }
              : undefined;
        return (
          <React.Fragment key={i}>
            <Box component="span" sx={sx}>
              {t.text}
            </Box>
            {i < tokens.length - 1 ? " " : null}
          </React.Fragment>
        );
      })}
    </Typography>
  );
};

const EntryDiffRow: React.FC<{ entry: EntryDiff }> = ({ entry }) => {
  const theme = useTheme();
  const meta = kindMeta[entry.kind];
  const shown = entry.after ?? entry.before!;
  const accent = theme.palette[meta.color].main;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        borderLeft: `3px solid ${accent}`,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
        <Chip size="small" label={meta.label} color={meta.color} variant="outlined" />
        <Typography variant="body2" fontWeight={600}>
          {formatSpeakerLabel(shown.speaker)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontFamily: "monospace", color: "text.secondary" }}
        >
          {formatTimestamp(shown.start)} – {formatTimestamp(shown.end)}
        </Typography>
      </Box>

      {entry.speakerChange && (
        <Typography variant="caption" sx={{ display: "block", mb: 0.25 }}>
          Speaker:{" "}
          <Box component="span" sx={{ color: "text.secondary" }}>
            {formatSpeakerLabel(entry.speakerChange.from)}
          </Box>{" "}
          → <strong>{formatSpeakerLabel(entry.speakerChange.to)}</strong>
        </Typography>
      )}
      {entry.rangeChange && (
        <Typography
          variant="caption"
          sx={{ display: "block", mb: 0.25, fontFamily: "monospace" }}
        >
          Time:{" "}
          <Box component="span" sx={{ color: "text.secondary" }}>
            {formatTimestamp(entry.rangeChange.from[0])}–
            {formatTimestamp(entry.rangeChange.from[1])}
          </Box>{" "}
          →{" "}
          <strong>
            {formatTimestamp(entry.rangeChange.to[0])}–
            {formatTimestamp(entry.rangeChange.to[1])}
          </strong>
        </Typography>
      )}
      {entry.textTokens && entry.textTokens.length > 0 && (
        <Box mt={0.5}>
          <WordDiff tokens={entry.textTokens} />
        </Box>
      )}
    </Box>
  );
};

/**
 * Save-confirmation modal: shows a before/after diff of the transcript (added /
 * removed / modified entries with word-level text highlighting) so the user can
 * verify every change before it's persisted.
 */
export const SaveDiffDialog: React.FC<SaveDiffDialogProps> = ({
  open,
  diff,
  isSaving,
  onConfirm,
  onCancel,
}) => {
  const { counts, entries } = diff;
  const summary = [
    counts.modified > 0 ? `${counts.modified} modified` : null,
    counts.added > 0 ? `${counts.added} added` : null,
    counts.removed > 0 ? `${counts.removed} removed` : null,
  ].filter(Boolean);

  return (
    <DialogShell
      open={open}
      onClose={onCancel}
      closeDisabled={isSaving}
      size="md"
      titleId="save-diff-dialog-title"
    >
      <DialogTitle id="save-diff-dialog-title" sx={{ pr: 6 }}>
        Review changes before saving
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          {summary.length
            ? `You're about to save ${summary.join(", ")} ${
                entries.length === 1 ? "entry" : "entries"
              }. Review the changes below.`
            : "No changes to save."}
        </Typography>
        <Stack spacing={1.25} divider={<Divider flexItem />}>
          {entries.map((entry) => (
            <EntryDiffRow key={`${entry.kind}-${entry.uid}`} entry={entry} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={isSaving}
          startIcon={<Close fontSize="small" />}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={isSaving || entries.length === 0}
          startIcon={
            isSaving ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              <Save fontSize="small" />
            )
          }
          sx={{ textTransform: "none" }}
        >
          Confirm &amp; save
        </Button>
      </DialogActions>
    </DialogShell>
  );
};
